import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles, categories, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-error-warning-line text-5xl text-foreground-300" />
            </div>
            <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article.id, article.category);
  const categoryLabel = categories.find((c) => c.id === article.category)?.label || article.category;
  const currentIndex = articles.findIndex((a) => a.id === article.id);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { '@type': 'Organization', name: '日本伦理d3tt' },
      publisher: { '@type': 'Organization', name: '日本伦理d3tt' },
      url: `https://eeunmsg.cn/article/${article.id}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://eeunmsg.cn/' },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://eeunmsg.cn/category' },
        { '@type': 'ListItem', position: 3, name: categoryLabel },
        { '@type': 'ListItem', position: 4, name: article.title },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${article.title} - 日本伦理d3tt`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://eeunmsg.cn/article/${article.id}`}
        ogType="article"
        schemaData={articleSchema}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-1 pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category' },
              { label: categoryLabel },
              { label: article.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                    {categoryLabel}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {article.date}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line" />
                    {article.readTime}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {article.views.toLocaleString()} 次浏览
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="w-full h-[400px] rounded-lg overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                  {prevArticle ? (
                    <Link
                      to={`/article/${prevArticle.id}`}
                      className="flex-1 flex items-center gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                    >
                      <i className="ri-arrow-left-line text-foreground-400 group-hover:text-primary-500 transition-colors" />
                      <div className="min-w-0">
                        <span className="text-xs text-foreground-400">上一篇</span>
                        <p className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {prevArticle.title}
                        </p>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex-1" />
                  )}
                  {nextArticle ? (
                    <Link
                      to={`/article/${nextArticle.id}`}
                      className="flex-1 flex items-center justify-end gap-3 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group text-right"
                    >
                      <div className="min-w-0">
                        <span className="text-xs text-foreground-400">下一篇</span>
                        <p className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {nextArticle.title}
                        </p>
                      </div>
                      <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors" />
                    </Link>
                  ) : (
                    <div className="flex-1" />
                  )}
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full bg-background-100 py-10">
              <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-5">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/50 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-full h-36 overflow-hidden">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1.5">
                          {rel.title}
                        </h4>
                        <span className="text-xs text-foreground-400">{rel.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>

      <style>{`
        .prose-custom h2 {
          font-size: 1.4rem;
          font-weight: 700;
          margin-top: 1.8rem;
          margin-bottom: 0.8rem;
          color: oklch(var(--foreground-900));
          font-family: var(--font-heading);
        }
        .prose-custom h3 {
          font-size: 1.15rem;
          font-weight: 600;
          margin-top: 1.4rem;
          margin-bottom: 0.6rem;
          color: oklch(var(--foreground-800));
          font-family: var(--font-heading);
        }
        .prose-custom p {
          margin-bottom: 1rem;
          font-size: 0.95rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
        }
        .prose-custom strong {
          font-weight: 600;
          color: oklch(var(--foreground-900));
        }
      `}</style>
    </>
  );
}